// Erweiterte Utility Functions für zusammenhängende Zeitblöcke
export const formatTime = (hour, half = false) => {
  const minutes = half ? "30" : "00";
  return `${hour.toString().padStart(2, "0")}:${minutes}`;
};

export const formatTimeFromSlot = (hour, half) => {
  return formatTime(hour, half);
};

export const formatDate = (date) => {
  return date.toISOString().split("T")[0];
};

export const getWeekDays = (date) => {
  const week = [];
  const startOfWeek = new Date(date);
  const day = startOfWeek.getDay();
  const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
  startOfWeek.setDate(diff);

  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    week.push(day);
  }
  return week;
};

export const formatWeekTitle = (weekDays) => {
  const start = weekDays[0];
  const end = weekDays[6];
  return `${start.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
  })} - ${end.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })}`;
};

export const formatDayHeader = (date) => {
  return date.toLocaleDateString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
};

export const isToday = (date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

export const addWeeks = (date, weeks) => {
  const newDate = new Date(date);
  newDate.setDate(date.getDate() + weeks * 7);
  return newDate;
};

export const getHoursRange = (startHour = 5, endHour = 23) => {
  return Array.from({ length: endHour - startHour }, (_, i) => i + startHour);
};

// NEUE FUNKTION: Erkennt zusammenhängende Zeitblöcke
export const getContiguousTimeBlocks = (selectedCells) => {
  if (selectedCells.length === 0) return [];

  // Gruppiere nach Datum - cell.date ist bereits ein String
  const cellsByDate = selectedCells.reduce((acc, cell) => {
    const dateKey = cell.date; // ENTFERNT: formatDate() - ist bereits String
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(cell);
    return acc;
  }, {});

  const timeBlocks = [];

  // Für jeden Tag: Finde zusammenhängende Blöcke
  Object.entries(cellsByDate).forEach(([dateKey, cells]) => {
    // Sortiere Zellen nach Zeit (Slot-basiert)
    const sortedCells = cells.sort((a, b) => {
      const aSlot = a.hour * 2 + (a.half ? 1 : 0);
      const bSlot = b.hour * 2 + (b.half ? 1 : 0);
      return aSlot - bSlot;
    });

    // Finde zusammenhängende Blöcke
    let currentBlock = [sortedCells[0]];

    for (let i = 1; i < sortedCells.length; i++) {
      const currentCell = sortedCells[i];
      const previousCell = sortedCells[i - 1];

      const currentSlot = currentCell.hour * 2 + (currentCell.half ? 1 : 0);
      const previousSlot = previousCell.hour * 2 + (previousCell.half ? 1 : 0);

      // Prüfe ob Zellen direkt aufeinander folgen
      if (currentSlot === previousSlot + 1) {
        // Zusammenhängend - zur aktuellen Block hinzufügen
        currentBlock.push(currentCell);
      } else {
        // Nicht zusammenhängend - aktuellen Block abschliessen
        timeBlocks.push({
          date: dateKey,
          cells: [...currentBlock],
          startTime: formatTimeFromSlot(
            currentBlock[0].hour,
            currentBlock[0].half
          ),
          endTime: formatTimeFromSlot(
            currentBlock[currentBlock.length - 1].hour +
              (currentBlock[currentBlock.length - 1].half ? 1 : 0),
            !currentBlock[currentBlock.length - 1].half
          ),
          duration: currentBlock.length * 0.5,
        });

        // Neuen Block starten
        currentBlock = [currentCell];
      }
    }

    // Letzten Block hinzufügen
    if (currentBlock.length > 0) {
      timeBlocks.push({
        date: dateKey,
        cells: [...currentBlock],
        startTime: formatTimeFromSlot(
          currentBlock[0].hour,
          currentBlock[0].half
        ),
        endTime: formatTimeFromSlot(
          currentBlock[currentBlock.length - 1].hour +
            (currentBlock[currentBlock.length - 1].half ? 1 : 0),
          !currentBlock[currentBlock.length - 1].half
        ),
        duration: currentBlock.length * 0.5,
      });
    }
  });

  // Sortiere Blöcke nach Datum und Zeit
  return timeBlocks.sort((a, b) => {
    if (a.date !== b.date) {
      return new Date(a.date) - new Date(b.date);
    }
    return a.startTime.localeCompare(b.startTime);
  });
};

// NEUE FUNKTION: Formatiert Zeitblöcke für die Anzeige
export const formatTimeBlocks = (timeBlocks) => {
  return timeBlocks
    .map((block) => {
      const date = new Date(block.date).toLocaleDateString("de-DE");
      return `${date}, ${block.startTime} - ${block.endTime}`;
    })
    .join("\n");
};

// NEUE FUNKTION: Berechnet Gesamtstunden aller Blöcke
export const getTotalHoursFromBlocks = (timeBlocks) => {
  return timeBlocks.reduce((total, block) => total + block.duration, 0);
};

// Bestehende Funktionen für Rückwärtskompatibilität
export const formatTimeRange = (selectedCells) => {
  const timeBlocks = getContiguousTimeBlocks(selectedCells);
  return formatTimeBlocks(timeBlocks);
};

export const getTotalHours = (selectedCells) => {
  return selectedCells.length * 0.5;
};

export const createTimeEntries = (selectedCells) => {
  const timeBlocks = getContiguousTimeBlocks(selectedCells);
  return timeBlocks.map((block) => ({
    date: block.date,
    startTime: block.startTime,
    endTime: block.endTime,
    duration: block.duration,
    cells: block.cells,
  }));
};

// Hilfsfunktionen
export const isWorkHour = (hour) => {
  return hour >= 8 && hour < 18;
};

export const getSlotFromTime = (hour, half) => {
  return hour * 2 + (half ? 1 : 0);
};

// Wandelt einen "HH:MM"-Zeitstring in den 30-Minuten-Slot-Index des Kalenders
// um (Minuten < 30 -> volle Stunde, sonst halbe Stunde). Zeiten, die nicht auf
// eine 30-Minuten-Grenze fallen, werden auf den Slot abgerundet.
export const getSlotFromTimeString = (timeStr) => {
  const [h, m] = (timeStr || "00:00").split(":").map(Number);
  return getSlotFromTime(h, m >= 30);
};

export const getWeekNumber = (date) => {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
};

export const getTimeFromSlot = (slot) => {
  const hour = Math.floor(slot / 2);
  const half = slot % 2 === 1;
  return { hour, half };
};

export const getDaysInRange = (startDateStr, endDateStr) => {
  if (!startDateStr || !endDateStr) return [];
  const days = [];
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);
  if (isNaN(start) || isNaN(end) || start > end) return [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(cursor.toISOString().split("T")[0]);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

export const getWeekStart = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Montag als Wochenstart
  start.setDate(diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

export const getWeekEnd = (date) => {
  const end = new Date(date);
  const day = end.getDay();
  const diff = end.getDate() - day + (day === 0 ? 0 : 7); // Sonntag als Wochenende
  end.setDate(diff);
  end.setHours(23, 59, 59, 999);
  return end;
};
